import { atom } from "nanostores";
import type { ChatDeleted, ChatHistory, ChatMessage } from "../gen/livesoup/v1/realtime_pb";
import type { PlotInfo, PlotRoleInfo } from "../net/api";
import { $roomPhase } from "../room/phase";
import { $playerPos, $session, type Presence } from "./stores";

export const MAX_LINES = 200;
export const NEAR_RADIUS = 30;
const STORAGE_KEY = "nomen.chatChannel";

export type ChatKind = "global" | "plot" | "near";
/** One chat line; the channel label is looked up when drawn, so it follows renamed or late-loaded plots. */
export interface ChatLine { id: string; channel: string; kind: ChatKind; senderName: string; body: string; at: number; receivedAt: number; mention: boolean }
export interface ChatChoice { id: string; label: string }

export const $chatLines = atom<ChatLine[]>([]);
export const $chatOpen = atom(false);
export const $chatChannel = atom("");
export interface ChatDraft { channel: string; body: string }
/** Text the next open puts back: a message the server refused, or one left half-typed. */
export const $chatDraft = atom<ChatDraft | null>(null);

export function channelKind(id: string): ChatKind {
  return id === "global" ? "global" : id.startsWith("plot:") ? "plot" : "near";
}

export function channelLabel(id: string, plots: PlotInfo[]): string {
  const kind = channelKind(id);
  if (kind === "global") return "全域";
  if (kind === "near") return "附近";
  return plots.find((p) => p.id === id.slice("plot:".length))?.name ?? "領地";
}

function toLine(m: ChatMessage, myName: string | null, now: number): ChatLine {
  return {
    id: m.id, channel: m.channelId, kind: channelKind(m.channelId), senderName: m.senderName, body: m.body,
    at: Number(m.createdAtMs), receivedAt: now, mention: !!myName && m.body.includes(`@${myName}`),
  };
}

const byTime = (a: ChatLine, b: ChatLine) => a.at - b.at || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

/** Adds lines, one per id, ordered by (at, id), keeping the newest MAX_LINES. */
export function mergeLines(cur: ChatLine[], add: ChatLine[]): ChatLine[] {
  const byId = new Map(cur.map((l) => [l.id, l] as const));
  for (const l of add) if (!byId.has(l.id)) byId.set(l.id, l);
  const all = [...byId.values()].sort(byTime);
  return all.slice(-MAX_LINES);
}

export function withinNear(pos: { x: number; z: number } | undefined, me: { x: number; z: number }): boolean {
  return !!pos && Math.hypot(pos.x - me.x, pos.z - me.z) <= NEAR_RADIUS;
}

/** Tab order (spec 2i §3.2): nearby, the estate I stand in, my other estates by name, global. */
export function chatChoices(presence: Presence | null, plots: PlotInfo[], roles: PlotRoleInfo[]): ChatChoice[] {
  const out: ChatChoice[] = [{ id: "", label: "附近" }];
  const here = presence ? plots.find((p) => p.id === presence.plotId && p.kind === "estate") : undefined;
  if (here) out.push({ id: `plot:${here.id}`, label: here.name });
  [...roles].filter((r) => r.id !== here?.id).sort((a, b) => a.name.localeCompare(b.name))
    .forEach((r) => out.push({ id: `plot:${r.id}`, label: r.name }));
  out.push({ id: "global", label: "全域" });
  return out;
}

export function nextChoice(choices: ChatChoice[], current: string): string {
  const i = choices.findIndex((c) => c.id === current);
  return choices[(i + 1) % choices.length]!.id;
}

export function pickChannel(saved: string | null, choices: ChatChoice[]): string {
  return saved !== null && choices.some((c) => c.id === saved) ? saved : "";
}

export function loadChannel(storage: Pick<Storage, "getItem"> | null): string | null {
  try { return storage?.getItem(STORAGE_KEY) ?? null; } catch { return null; }
}

export function saveChannel(storage: Pick<Storage, "setItem"> | null, id: string): void {
  try { storage?.setItem(STORAGE_KEY, id); } catch { /* storage blocked: start on 附近 next time */ }
}

const myName = () => $session.get()?.display_name ?? null;

export function onChatMessage(m: ChatMessage): void {
  if (m.channelId === "" && ($roomPhase.get() !== "lobby" || !withinNear(m.pos, $playerPos.get()))) return; // nobody is near you in the room
  $chatLines.set(mergeLines($chatLines.get(), [toLine(m, myName(), Date.now())]));
}

/** Nearby talk is refused in the room, as the server would (spec 2o §2.5). */
export function refuseNearInRoom(channel: string): boolean {
  return channel === "" && $roomPhase.get() !== "lobby";
}

/**
 * A channel's history is the truth between its oldest and newest line, in the server's (at, id)
 * order: lines there that it lacks were deleted while offline. Lines outside that span stay, since a
 * live message can arrive while the history is still being read; an empty history drops nothing.
 */
export function onChatHistory(h: ChatHistory): void {
  const now = Date.now(), name = myName();
  const add = h.messages.map((m) => toLine(m, name, now)).sort(byTime);
  const keep = new Set(add.map((l) => l.id));
  const first = add[0], last = add[add.length - 1];
  const stale = (l: ChatLine) => !!first && !!last && l.channel === h.channelId && !keep.has(l.id) && byTime(l, first) >= 0 && byTime(l, last) <= 0;
  $chatLines.set(mergeLines($chatLines.get().filter((l) => !stale(l)), add));
}

export function onChatDeleted(d: ChatDeleted): void {
  $chatLines.set($chatLines.get().filter((l) => l.id !== d.id));
}

let lastSent: ChatDraft | null = null;

/** Remembers what went out, so a refusal can hand it back. */
export function noteSent(channel: string, body: string): void {
  lastSent = { channel, body };
}

/** Chat errors answer the last send (spec 2i §4.3): its text becomes the draft instead of being lost. */
export function onChatError(code: string): void {
  if (!lastSent || !(code.startsWith("CHAT_") || code === "CHANNEL_NOT_FOUND")) return;
  $chatDraft.set(lastSent);
  lastSent = null;
}
